/**
 * EditorToolbar.tsx
 *
 * Editor toolbar with AI assistant, emoji picker, and character count.
 *
 * Features:
 * - AI magic wand for content generation
 * - Emoji picker
 * - Character count with platform limits
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Tooltip } from '@/components/ui/Tooltip';
import { EmojiPlugin } from './EmojiPlugin';
import { AIMagicPanel } from './AIMagicPanel';
import { CompactCharacterCount } from './CompactCharacterCount';


interface EditorToolbarProps {
  /**
   * Custom class name
   */
  className?: string;

  /**
   * Current caption text
   */
  caption?: string;

  /**
   * Callback when AI generates caption
   */
  onCaptionGenerated?: (text: string) => void;

  /**
   * Selected channel IDs for AI context
   */
  selectedChannels?: number[];

  /**
   * Callback when AI inserts an image (from Unsplash)
   */
  onInsertImage?: (url: string, credit: string, downloadUrl: string) => void;

  /**
   * Selected platforms for character counting
   */
  platforms?: string[];
}

/**
 * EditorToolbar component
 * Renders AI, emoji, and character count for the editor
 */
export function EditorToolbar({
  className = '',
  caption = '',
  onCaptionGenerated,
  selectedChannels = [],
  onInsertImage,
  platforms = [],
}: EditorToolbarProps): JSX.Element {
  const { t } = useTranslation('ai');
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [showImageSearch, setShowImageSearch] = useState(false);

  // Two-phase animation: mounted keeps DOM alive, visible triggers CSS transition
  const [aiMounted, setAiMounted] = useState(false);
  const [aiVisible, setAiVisible] = useState(false);

  useEffect(() => {
    if (aiPanelOpen) {
      setAiMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAiVisible(true));
      });
    } else {
      setAiVisible(false);
      const timer = setTimeout(() => setAiMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [aiPanelOpen]);

  const handleAiPanelClose = useCallback((open: boolean) => {
    if (!open) {
      setAiVisible(false);
      setTimeout(() => setAiPanelOpen(false), 300);
    } else {
      setAiPanelOpen(true);
    }
  }, []);

  return (
    <>
      <div
        className={`flex items-center gap-1 p-2 max-sm:p-1 border-b border-gray-200 bg-off-white rounded-t-lg ${className}`}
      >
        {/* Magic Wand / AI button */}
        <Tooltip content={t('ai_briefing_magic_ed_label')} side="bottom" disabled={aiPanelOpen}>
          <button
            type="button"
            onClick={() => setAiPanelOpen(!aiPanelOpen)}
            className={`flex items-center justify-center w-8 h-8 max-sm:w-[44px] max-sm:h-[44px] rounded transition-colors ${
              aiPanelOpen
                ? 'bg-primary-50 text-primary-600'
                : 'text-gray-600 hover:bg-soft-mint'
            }`}
            aria-label={t('ai_briefing_magic_ed_label')}
          >
            <Icon name={ICONS.MAGIC_WAND} size="sm" />
          </button>
        </Tooltip>

        {/* Separator */}
        <div className="w-px h-4 bg-gray-300 mx-0.5" aria-hidden="true" />

        {/* Emoji picker */}
        <EmojiPlugin />

        {/* Character count (includes separator when visible) */}
        <CompactCharacterCount platforms={platforms} showSeparator />
      </div>

      {/* Magic Ed Overlay — card-on-card effect, rendered inline (inside Radix Dialog) */}
      {aiMounted && (
        <div className="fixed inset-0 z-modal-nested flex items-center justify-center lg:p-4">
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ${aiVisible ? 'opacity-100' : 'opacity-0'}`}
            aria-hidden="true"
          />

          {/* Magic Ed Card — slide up on mobile, scale on desktop */}
          <div className={`relative z-10 w-full h-full lg:w-auto lg:h-auto transition-all duration-300 ease-out ${
            aiVisible
              ? 'opacity-100 translate-y-0 lg:translate-y-0 lg:scale-100'
              : 'opacity-0 translate-y-8 lg:translate-y-0 lg:scale-95'
          }`}>
            <AIMagicPanel
              open={aiPanelOpen}
              onOpenChange={handleAiPanelClose}
              caption={caption}
              onCaptionGenerated={(text) => {
                if (onCaptionGenerated) {
                  onCaptionGenerated(text);
                }
              }}
              selectedChannels={selectedChannels}
              onInsertImage={onInsertImage}
              topic={aiTopic}
              onTopicChange={setAiTopic}
              showImageSearch={showImageSearch}
              onShowImageSearchChange={setShowImageSearch}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default EditorToolbar;
